/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/support-view.tsx`.
 *
 * Health strip + open Customer Success tasks behind `crm.views.support`.
 * The Next view rendered open tasks through UnifiedIssuesList (RO-3-00g /
 * #44474). Until that port lands, open tasks render as a plain linked list —
 * same data, thinner chrome.
 */

import {
  Button,
  Card,
  CardContent,
  EmptyState,
  StatStrip,
  type StatStripItem,
} from "@excellent/ui";
import { LifeBuoy } from "@excellent/ui/icons";
import { RouterLink } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";

export function SupportView(_props: { basePath: string }) {
  const { data, error, isLoading } = useApi(crm.views.support, {});

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  if (error || !data) {
    return (
      <EmptyState
        icon={<LifeBuoy className="size-5" />}
        title="Could not load support"
        description={error?.message ?? "Try refreshing."}
      />
    );
  }

  const total = data.customers_total;
  const atRisk = data.customers_atrisk;
  const atRiskPct = total > 0 ? Math.round((atRisk / total) * 100) : 0;
  const healthStats: StatStripItem[] = [
    { title: "Customers", value: String(total) },
    { title: "Healthy (active <30d)", value: String(data.customers_healthy) },
    {
      title: "At risk",
      value: String(atRisk),
      description: `${atRiskPct}% of customers`,
    },
  ];
  const open = data.open_tasks;

  return (
    <div className="flex flex-col gap-6">
      {total > 0 ? <StatStrip items={healthStats} variant="standalone" /> : null}
      {open.length === 0 ? (
        <EmptyState
          icon={<LifeBuoy className="size-5" />}
          title="Support cases are tasks"
          description="A support request lives as a task in a project's Customer Success section. Cases ride the same queue, planner, and reviews as the rest of your work — there's no separate support inbox. Open a project's Customer Success section to log one."
          action={
            <Button asChild variant="outline">
              <RouterLink href="/work/issues">View all issues</RouterLink>
            </Button>
          }
        />
      ) : (
        <Card className="gap-0 overflow-hidden py-0">
          <CardContent className="px-0">
            <ul className="divide-y divide-border/40">
              {open.map((t) => (
                <li key={t.id} className="flex items-center gap-3 px-4 py-3 type-body">
                  <RouterLink
                    href={`/work/issues?task=${t.id}`}
                    className="font-medium text-foreground hover:underline"
                  >
                    {t.title}
                  </RouterLink>
                  <span className="text-muted-foreground">{t.status}</span>
                  {t.project_slug ? (
                    <span className="ml-auto text-muted-foreground">{t.project_slug}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
